// Analytics events for ACCESS Registration (webreg).
// Hello Heart events taxonomy (RND-37449 / analytics-instrumentation.md):
//   - Event names: snake_case, sent verbatim to Mixpanel (e.g. view_medicare_coverage).
//     The readable "View Medicare Coverage" display name is configured in Mixpanel
//     Lexicon, not here. Enforced by events.lock.test.ts.
//   - Screen views: view_ + feature/flow. User actions: verb first. Server-side
//     outcomes on the user's behalf: passive (…_completed_successfully).
//   - Property keys: snake_case (e.g. source, gating_answer, webreg_user_id)
// Mixpanel stays curated (screens + intentful CTAs/outcomes) — not a tap firehose.

// ============ SCREEN VIEWS ============
export const VIEW_WELCOME = 'view_welcome';

// ============ SCREEN VIEWS, WEBREG 2.0 ============
export const VIEW_WREG_DETAILS = 'view_wreg_details';
export const VIEW_WREG_HEALTH_CHECK = 'view_wreg_health_check';
export const VIEW_WREG_PROGRAMS = 'view_wreg_programs';
export const VIEW_WREG_TERMS = 'view_wreg_terms';
export const VIEW_WREG_SWITCH_CONSENT = 'view_wreg_switch_consent';
export const VIEW_WREG_LAB_RESULTS = 'view_wreg_lab_results';
export const VIEW_WREG_SHIPPING = 'view_wreg_shipping';
export const VIEW_WREG_ALL_SET = 'view_wreg_all_set';
export const VIEW_WREG_NOT_ELIGIBLE = 'view_wreg_not_eligible';
export const VIEW_WREG_ALREADY_ENROLLED = 'view_wreg_already_enrolled';
export const VIEW_WREG_CONTROL_GROUP = 'view_wreg_control_group';
export const VIEW_WREG_CONFIRM_DETAILS = 'view_wreg_confirm_details';
export const VIEW_WREG_NO_QUALIFYING_CONDITION = 'view_wreg_no_qualifying_condition';
export const VIEW_WREG_ERROR = 'view_wreg_error';
export const VIEW_WREG_SWITCH_COMPLETE = 'view_wreg_switch_complete';
export const VIEW_WREG_OUTREACH = 'view_wreg_outreach';
export const VIEW_WREG_LABS_PENDING = 'view_wreg_labs_pending';
export const VIEW_WREG_DECLINED = 'view_wreg_declined';

// ============ OUTCOMES, WEBREG 2.0 ============
export const WREG_REGISTRATION_COMPLETED = 'wreg_registration_completed_successfully';
export const WREG_REGISTRATION_FAILED = 'wreg_registration_failed';
export const WREG_REGISTRATION_ENDED = 'wreg_registration_ended';

/**
 * Closed set of `reason` values on WREG_REGISTRATION_FAILED. Six were agreed
 * with the dashboard owner on 2026-09-01; `promo_cap_reached` is the seventh,
 * added by RND-38229 so a registration cap is not counted as a generic signup
 * failure. Locked by events.lock.test.ts and documented in docs/ANALYTICS.md.
 */
export const WREG_FAILURE_REASONS = {
  INVALID_DOB: 'invalid_dob',
  SIGNUP_FAILED: 'signup_failed',
  ELIGIBILITY_REJECTED: 'eligibility_rejected',
  ALIGNMENT_REJECTED: 'alignment_rejected',
  CMS_UNAVAILABLE: 'cms_unavailable',
  CLIENT_ERROR: 'client_error',
  PROMO_CAP_REACHED: 'promo_cap_reached',
} as const;

export type WregFailureReason =
  (typeof WREG_FAILURE_REASONS)[keyof typeof WREG_FAILURE_REASONS];

// ============ BUTTON / CTA ============
export const START_ONBOARDING = 'start_onboarding';
export const NEED_HELP = 'need_help';

// ============ HEADER ============
export const CHANGE_LANGUAGE = 'change_language';
export const LANGUAGE_CHANGED = 'language_changed';

// ============ SUPPORT MODAL ============
export const VIEW_SUPPORT = 'view_support';
export const EMAIL_SUPPORT = 'email_support';
export const CALL_SUPPORT = 'call_support';
export const SUPPORT_CLOSE = 'close_support';
export const CHATBOT = 'open_chatbot';

// ============ API ERROR MODAL ============
export const VIEW_API_ERROR = 'view_api_error';

// ============ SHIPPING ============
export const SHIPPING_ADDRESS_UNCONFIRMED = 'shipping_address_unconfirmed';

// PHI NOTE: raw PHQ-9/GAD-7 scores and the Q9 response are intentionally NOT
// emitted as event properties. Only booleans + non-PHI resource ids are sent.
// Do not emit a crisis_gate_shown event — it is a near-direct PHI proxy for Q9.

// ============ ERRORS ============
export const ERROR_EVENT_NAME = 'access_error';
export const ERROR_API_CALL = 'api_call_failed';
export const RECAPTCHA_VALIDATION_FAILED = 'recaptcha_validation_failed';
export const ERROR_DECRYPTION_FAILED = 'decryption_failed';
export const ERROR_STORAGE_SAVE_FAILED = 'storage_save_failed';
export const ERROR_STORAGE_LOAD_FAILED = 'storage_load_failed';

/**
 * Closed set of CTA-origin values for the `source` property. Locked alongside
 * the event names so an inline literal typo can't open a silent new bucket in
 * Mixpanel (`docs/ANALYTICS.md` enumerates the same set).
 *
 * GATING_ECKM is the CMS track id rather than snake_case, kept verbatim because it
 * is shared with the track ids the CMS API expects.
 */
export const EVENT_SOURCES = {
  WELCOME: 'welcome',
  HEADER: 'header',
  MEDICARE_COVERAGE: 'medicare_coverage',
  CREATE_ACCOUNT: 'create_account',
  CHECK_SMS: 'check_sms',
  GATING_ECKM: 'eCKM',
} as const;

export type EventSource = (typeof EVENT_SOURCES)[keyof typeof EVENT_SOURCES];

/** All Mixpanel event name constants — used by events.lock.test.ts */
export const ALL_EVENTS = {
  WREG_REGISTRATION_COMPLETED,
  WREG_REGISTRATION_FAILED,
  WREG_REGISTRATION_ENDED,
  VIEW_WREG_DETAILS,
  VIEW_WREG_HEALTH_CHECK,
  VIEW_WREG_PROGRAMS,
  VIEW_WREG_TERMS,
  VIEW_WREG_SWITCH_CONSENT,
  VIEW_WREG_LAB_RESULTS,
  VIEW_WREG_SHIPPING,
  VIEW_WREG_ALL_SET,
  VIEW_WREG_NOT_ELIGIBLE,
  VIEW_WREG_ALREADY_ENROLLED,
  VIEW_WREG_CONTROL_GROUP,
  VIEW_WREG_CONFIRM_DETAILS,
  VIEW_WREG_NO_QUALIFYING_CONDITION,
  VIEW_WREG_ERROR,
  VIEW_WREG_SWITCH_COMPLETE,
  VIEW_WREG_OUTREACH,
  VIEW_WREG_LABS_PENDING,
  VIEW_WREG_DECLINED,
  VIEW_WELCOME,
  START_ONBOARDING,
  NEED_HELP,
  CHANGE_LANGUAGE,
  LANGUAGE_CHANGED,
  VIEW_SUPPORT,
  EMAIL_SUPPORT,
  CALL_SUPPORT,
  SUPPORT_CLOSE,
  CHATBOT,
  VIEW_API_ERROR,
  SHIPPING_ADDRESS_UNCONFIRMED,
  ERROR_EVENT_NAME,
  ERROR_API_CALL,
  RECAPTCHA_VALIDATION_FAILED,
  ERROR_DECRYPTION_FAILED,
  ERROR_STORAGE_SAVE_FAILED,
  ERROR_STORAGE_LOAD_FAILED,
} as const;
